'use client'

import { useAudioPlayer } from '@/components/client/audio/use-audio-player'
import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { tw } from '@/lib/utils'

const SIDE_STYLES = {
  a: { discTint: 'bg-lapis/15 text-lapis', bar: 'bg-lapis', chipPlaying: 'border-lapis bg-lapis/3 text-lapis' },
  b: { discTint: 'bg-navy/10 text-navy', bar: 'bg-navy', chipPlaying: 'border-navy bg-navy/3 text-navy' },
} as const

interface LabAudioButtonProps {
  url: string
  hearingAidName: string
  label: string
  group: string
  // Which product the recording belongs to; omitted for the shared
  // open-ear reference clip (neutral styling).
  side?: 'a' | 'b'
  className?: string
}

const BAR_HEIGHTS = ['45%', '90%', '60%']

// Tiny equalizer that animates while this clip is playing — state, not
// decoration. Static bars under reduced motion.
const EqBars = () => (
  <span aria-hidden className="inline-flex h-3 items-end gap-[2px]">
    {BAR_HEIGHTS.map((height, i) => (
      <span
        key={i}
        className="w-[2px] rounded-full bg-current [animation:soundBar_1.2s_ease-in-out_infinite] motion-reduce:animate-none"
        style={{ height, animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </span>
)

export const LabAudioButton = ({ url, hearingAidName, label, group, side, className }: LabAudioButtonProps) => {
  const { audioRef, status, toggle, isMounted, audioProps } = useAudioPlayer({ url, group })

  if (!isMounted) return null

  const isPlaying = status.isPlaying
  // Idle discs are a soft tint of the side color (heavy solid circles read as
  // three competing dark buttons per row); the solid fill is reserved for the
  // playing state, where it IS the state indicator.
  const discIdle = side ? SIDE_STYLES[side].discTint : 'bg-navy/10 text-navy'
  const discPlaying = side ? SIDE_STYLES[side].bar : 'bg-navy'
  const playingChip = side ? SIDE_STYLES[side].chipPlaying : 'border-navy bg-navy/3 text-navy'

  return (
    <button
      type="button"
      data-tracking="ignore"
      onClick={toggle}
      aria-label={`${label} – ${hearingAidName}`}
      aria-pressed={isPlaying}
      className={tw(
        'inline-flex cursor-pointer items-center gap-2 rounded-full border py-1 pr-3.5 pl-1 transition-colors',
        isPlaying ? playingChip : 'border-navy/15 hover:border-lapis bg-white',
        className
      )}
    >
      <span
        className={tw(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors',
          isPlaying ? tw(discPlaying, 'text-white') : discIdle
        )}
      >
        {/* fill-current so the glyph inherits the disc's text color — white on
            the solid playing disc (otherwise it's a black glyph on dark navy),
            the side tint on the idle disc. */}
        {isPlaying ? (
          <SvgPause aria-hidden className="h-3 w-3 fill-current" />
        ) : (
          <SvgPlay aria-hidden className="ml-0.5 h-3 w-3 fill-current" />
        )}
      </span>
      <span data-audio-label className="text-navy text-sm font-medium">
        {label}
      </span>
      {isPlaying ? <EqBars /> : null}
      <audio ref={audioRef} {...audioProps} />
    </button>
  )
}
